import fetcher from 'api/fetcher';
import { AxiosError } from 'axios';
import Debug from 'utils/debug';
import { getXRequestId } from 'utils/getXRequestId';

import { translate } from 'hooks/translation/useTrls';

import {
    CampaignFilter,
    CampaignsFilters,
    setCampaignsFilters,
    setIsCampaignsFiltersLoading,
} from 'store/models/campaignsFilters';
import { Trls } from 'store/models/translations';
import { AppThunkAction } from 'store/utils';

enum CampaignStatusFilterValue {
    Active = 'ACTIVE',
    Inactive = 'INACTIVE',
}

enum ProductTypeFilterValue {
    Cpc = 'CLICKME_CPC',
    Auction = 'CLICKME_SEARCH_AUCTION',
}

enum BudgetStatusFilterValue {
    Low = 'LOW',
    Exhausted = 'EXHAUSTED',
}

enum ModerationStatusFilterValue {
    InProcess = 'IN_PROCESS',
    PartlySuccess = 'PARTLY_SUCCESS',
    Failed = 'FAILED',
}

interface CampaignsFiltersDTO {
    [CampaignFilter.ProductTypes]?: string[];
    [CampaignFilter.CampaignStatuses]?: string[];
    [CampaignFilter.BudgetStatuses]?: string[];
    [CampaignFilter.ModerationStatuses]?: string[];
    [CampaignFilter.Areas]?: Record<string, string>;
    [CampaignFilter.ProfessionalRoles]?: Record<string, string>;
    [CampaignFilter.CreatedByUserIds]?: Record<string, string>;
}

const FILTER_NAME_MAP: Partial<Record<CampaignFilter, Record<string, string> | null>> = {
    [CampaignFilter.CampaignStatuses]: {
        [CampaignStatusFilterValue.Active]: 'CampaignsFiltersPanel.CampaignStatusFilterValue.active',
        [CampaignStatusFilterValue.Inactive]: 'CampaignsFiltersPanel.CampaignStatusFilterValue.inactive',
    },
    [CampaignFilter.ProductTypes]: {
        [ProductTypeFilterValue.Cpc]: 'CampaignsFiltersPanel.ProductTypeFilterValue.cpc',
        [ProductTypeFilterValue.Auction]: 'CampaignsFiltersPanel.ProductTypeFilterValue.auction',
    },
    [CampaignFilter.BudgetStatuses]: {
        [BudgetStatusFilterValue.Low]: 'CampaignsFiltersPanel.BudgetStatusFilterValue.low',
        [BudgetStatusFilterValue.Exhausted]: 'CampaignsFiltersPanel.BudgetStatusFilterValue.exhausted',
    },
    [CampaignFilter.ModerationStatuses]: {
        [ModerationStatusFilterValue.InProcess]: 'CampaignsFiltersPanel.ModerationStatusFilterValue.inProcess',
        [ModerationStatusFilterValue.PartlySuccess]: 'CampaignsFiltersPanel.ModerationStatusFilterValue.partlySuccess',
        [ModerationStatusFilterValue.Failed]: 'CampaignsFiltersPanel.ModerationStatusFilterValue.failed',
    },
    [CampaignFilter.Areas]: null,
    [CampaignFilter.ProfessionalRoles]: null,
    [CampaignFilter.CreatedByUserIds]: null,
};

const convertArrayToCampaignFilter = (
    array: string[],
    trlKeys: Trls,
    filterName: CampaignFilter
): Record<string, string> =>
    array.reduce<Record<string, string>>((accumulator, value) => {
        const filterMap = FILTER_NAME_MAP[filterName];
        if (filterMap && value in filterMap) {
            accumulator[value] = translate(trlKeys, filterMap[value]);
        }
        return accumulator;
    }, {});

const prepareFilters = (filters: CampaignsFiltersDTO, trlKeys: Trls): CampaignsFilters => {
    const {
        productTypes,
        campaignStatuses,
        budgetStatuses,
        moderationStatuses,
        areas,
        professionalRoles,
        createdByUserIds,
    } = filters;

    return {
        [CampaignFilter.ProductTypes]: convertArrayToCampaignFilter(
            productTypes || [],
            trlKeys,
            CampaignFilter.ProductTypes
        ),
        [CampaignFilter.CampaignStatuses]: convertArrayToCampaignFilter(
            campaignStatuses || [],
            trlKeys,
            CampaignFilter.CampaignStatuses
        ),
        [CampaignFilter.BudgetStatuses]: convertArrayToCampaignFilter(
            budgetStatuses || [],
            trlKeys,
            CampaignFilter.BudgetStatuses
        ),
        [CampaignFilter.ModerationStatuses]: convertArrayToCampaignFilter(
            moderationStatuses || [],
            trlKeys,
            CampaignFilter.ModerationStatuses
        ),
        [CampaignFilter.Areas]: areas,
        [CampaignFilter.ProfessionalRoles]: professionalRoles,
        [CampaignFilter.CreatedByUserIds]: createdByUserIds,
    };
};

export const fetchFiltersList = ({
    abortSignal,
}: {
    abortSignal: AbortSignal;
}): AppThunkAction<Promise<void> | null> => {
    return (dispatch, getState) => {
        const trlKeys = getState().translations.trls;
        dispatch(setIsCampaignsFiltersLoading(true));

        return fetcher
            .get<unknown, CampaignsFiltersDTO>(`/api/v1/campaigns/filter`, {
                timeout: 30000,
                signal: abortSignal,
            })
            .then((filters) => {
                const preparedFilters = prepareFilters(filters, trlKeys);
                dispatch(setCampaignsFilters(preparedFilters));
            })
            .catch((response: AxiosError<Error>) => {
                dispatch(
                    setCampaignsFilters({
                        productTypes: {},
                    })
                );

                Debug.log('error out', 'filters load error', {
                    message: response.message,
                    xRequestId: getXRequestId(response),
                });
            })
            .finally(() => {
                dispatch(setIsCampaignsFiltersLoading(false));
            });
    };
};
